import React, { useState } from "react";
import Tooltip from "../../ui/Tooltip";
import InfoModal from "../../ui/InfoModal";
import LazyImage from "../../ui/LazyImage";
import { ImageIcon, ZoomIn, Check, ChevronDown, AlertCircle, X } from "lucide-react";

const apiURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const fixColorCode = (hex) => {
    if (!hex) return '#C5A075';
    let c = hex.trim();
    if (!c.startsWith('#')) c = '#' + c;
    if (c.length === 7 || c.length === 4) return c;
    if (c.length > 1 && c.length < 7) return c.padEnd(7, c.charAt(c.length - 1));
    return c;
};

const StrapOptionPanel = ({
    activeStrapOption,
    tempStrapValue,
    setTempStrapValue,
    activeSubTab,
    setActiveSubTab,
    isOrderFinished,
    showDescModal,
    setShowDescModal,
    onBack,
    onConfirm,
}) => {
    const [zoomImgSrc, setZoomImgSrc] = useState(null);

    if (!activeStrapOption) return null;

    const isImageChoice = activeStrapOption.type === 'image-choice';
    const isImageSelect = activeStrapOption.type === 'image-select';

    const filteredChoices = activeStrapOption.useTabs && activeSubTab
        ? (activeStrapOption.choices || []).filter(c => c.tab === activeSubTab)
        : (activeStrapOption.choices || []);

    let body = null;

    if (isImageSelect) {
        body = (
            <div className="img-choice-grid">
                {filteredChoices.map((choice, idx) => {
                    const choiceKey = choice.value || choice._id || String(idx);
                    const isChosen = tempStrapValue === choiceKey;
                    const imgSrc = choice.imageUrl
                        ? (choice.imageUrl.startsWith('http') ? choice.imageUrl : `${apiURL}${choice.imageUrl}`)
                        : null;
                    return (
                        <button
                            key={choiceKey}
                            type="button"
                            className={`img-choice-card${isChosen ? ' chosen' : ''}`}
                            onClick={() => !isOrderFinished && setTempStrapValue(choiceKey)}
                            disabled={isOrderFinished}
                        >
                            <div className="img-choice-label-container">
                                <div className="img-choice-label">
                                    {choice.label}
                                    {choice.tooltip && (
                                        <Tooltip text={choice.tooltip}>
                                            <span style={{ border: '1px solid #C5A075', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#C5A075', fontSize: '9px', fontWeight: '700', cursor: 'help', flexShrink: 0, lineHeight: 1, marginLeft: '4px', letterSpacing: 'normal', textTransform: 'none', transform: 'translateY(-1px)' }}>?</span>
                                        </Tooltip>
                                    )}
                                </div>
                                {choice.priceAddon > 0 && (
                                    <div className="img-choice-price">({choice.priceAddon}$)</div>
                                )}
                            </div>
                            <div className="img-choice-img-wrap">
                                {imgSrc
                                    ? <LazyImage src={imgSrc} alt={choice.label} />
                                    : <ImageIcon size={29} color="#C4B5A5" />
                                }
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    } else if (isImageChoice) {
        body = (
            <div className="img-choice-grid-2col">
                {filteredChoices.map((choice, idx) => {
                    const choiceKey = choice.value || choice._id || String(idx);
                    const isChosen = tempStrapValue === choiceKey;
                    const imgSrc = choice.imageUrl
                        ? (choice.imageUrl.startsWith('http') ? choice.imageUrl : `${apiURL}${choice.imageUrl}`)
                        : null;
                    return (
                        <button
                            key={choiceKey}
                            type="button"
                            className={`img-choice-card-box${isChosen ? ' chosen' : ''}`}
                            onClick={() => !isOrderFinished && setTempStrapValue(choiceKey)}
                            disabled={isOrderFinished}
                        >
                            {imgSrc && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setZoomImgSrc(imgSrc); }}
                                    style={{ position: 'absolute', top: 8, right: 8, background: '#111', color: '#fff', borderRadius: 4, padding: '4px 6px', fontSize: '0.7rem', border: 'none', cursor: 'pointer', lineHeight: 1, zIndex: 1 }}
                                >
                                    <ZoomIn size={12} />
                                </button>
                            )}
                            <div className="img-choice-img-wrap-sm">
                                {imgSrc
                                    ? <LazyImage src={imgSrc} alt={choice.label} />
                                    : <ImageIcon size={24} color="#C4B5A5" />
                                }
                            </div>
                            <div className="img-choice-label-container">
                                <div className="img-choice-label">
                                    {choice.label}
                                    {choice.tooltip && (
                                        <Tooltip text={choice.tooltip}>
                                            <span style={{ border: '1px solid #C5A075', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#C5A075', fontSize: '9px', fontWeight: '700', cursor: 'help', flexShrink: 0, lineHeight: 1, marginLeft: '4px', letterSpacing: 'normal', textTransform: 'none', transform: 'translateY(-1px)' }}>?</span>
                                        </Tooltip>
                                    )}
                                </div>
                                {choice.priceAddon > 0 && (
                                    <div className="img-choice-price">({choice.priceAddon}$)</div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    } else if (activeStrapOption.type === 'color-picker') {
        body = (
            <div className="color-picker-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '24px 20px' }}>
                {activeStrapOption.hasOnTone && (
                    <button
                        type="button"
                        className="color-picker-item"
                        onClick={() => !isOrderFinished && setTempStrapValue('on-tone')}
                        disabled={isOrderFinished}
                        style={{ background: 'none', border: 'none', padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: isOrderFinished ? 'default' : 'pointer', width: '100px' }}
                        title="Match the leather color"
                    >
                        <div className="color-circle-wrap" style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)', border: tempStrapValue === 'on-tone' ? '4px solid #1A110B' : '1.5px solid #EAE4DA', boxShadow: tempStrapValue === 'on-tone' ? '0 0 0 2px #C5A075' : '0 3px 12px rgba(0,0,0,0.07)', position: 'relative', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {tempStrapValue === 'on-tone' && <Check size={32} color="#fff" style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.4))' }} />}
                        </div>
                        <span style={{ fontSize: '0.82rem', color: tempStrapValue === 'on-tone' ? '#1A110B' : '#6B5A4A', fontWeight: tempStrapValue === 'on-tone' ? '700' : '500', textAlign: 'center', marginTop: '4px' }}>
                            On tone
                        </span>
                    </button>
                )}
                {filteredChoices.map((choice, idx) => {
                    const choiceKey = choice.value || choice._id || String(idx);
                    const isChosen = tempStrapValue === choiceKey;
                    const displayColor = fixColorCode(choice.colorCode);
                    return (
                        <button
                            key={choiceKey}
                            type="button"
                            className="color-picker-item"
                            onClick={() => !isOrderFinished && setTempStrapValue(choiceKey)}
                            disabled={isOrderFinished}
                            style={{ background: 'none', border: 'none', padding: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', cursor: isOrderFinished ? 'default' : 'pointer', width: '100px' }}
                            title={choice.tooltip || choice.label}
                        >
                            <div className="color-circle-wrap" style={{ width: '72px', height: '72px', borderRadius: '50%', backgroundColor: displayColor, border: isChosen ? '4px solid #1A110B' : '1.5px solid #EAE4DA', boxShadow: isChosen ? '0 0 0 2px #C5A075' : '0 3px 12px rgba(0,0,0,0.07)', position: 'relative', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            </div>
                            <span style={{ fontSize: '0.78rem', color: isChosen ? '#1A110B' : '#6B5A4A', fontWeight: isChosen ? '700' : '500', textAlign: 'center' }}>
                                {choice.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    } else if (activeStrapOption.type === 'custom-text') {
        const currentRaw = tempStrapValue || '';
        const parts = currentRaw.split('|');
        const initialText = parts[0].trim();
        const initialAddon = parts.length > 1 ? parts[1].trim() : '';
        const isAddonActive = initialAddon.startsWith('[Addon]');
        const textVal = initialText;

        const updateValue = (newText, newAddonActive) => {
            let finalVal = newText.trim();
            if (newAddonActive) {
                const addonPart = `[Addon] ${activeStrapOption.addonLabel || 'Enabled'}`;
                finalVal = finalVal ? `${finalVal} | ${addonPart}` : `| ${addonPart}`;
            }
            setTempStrapValue(finalVal);
        };

        body = (
            <div style={{ padding: '24px 20px' }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    {activeStrapOption.textInstruction && (
                        <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1A110B', marginBottom: '2px' }}>
                            {activeStrapOption.textInstruction}
                        </div>
                    )}
                    {activeStrapOption.textSubtitle && (
                        <div style={{ fontSize: '0.8rem', color: '#1A110B' }}>
                            {activeStrapOption.textSubtitle}
                        </div>
                    )}
                </div>
                <div style={{ marginBottom: '24px' }}>
                    <input
                        type="text"
                        value={textVal}
                        onChange={(e) => {
                            if (isOrderFinished) return;
                            const val = e.target.value;
                            const limit = activeStrapOption.textLimit || 10;
                            if (val.length <= limit) updateValue(val, isAddonActive);
                        }}
                        disabled={isOrderFinished}
                        placeholder={activeStrapOption.textPlaceholder || 'Limit 10 character'}
                        style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #D6C5B3', fontSize: '0.9rem', color: '#1A110B', width: '100%', textAlign: 'center', outline: 'none', backgroundColor: '#fff', boxShadow: 'none' }}
                    />
                </div>
                {activeStrapOption.hasAddonToggle && (
                    <>
                        <hr style={{ borderColor: '#EAE4DA', margin: '0 0 20px 0' }} />
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1A110B' }}>
                                {activeStrapOption.addonLabel || 'No engrave logo'}
                            </div>
                            {activeStrapOption.addonPrice > 0 && (
                                <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1A110B', marginTop: '2px' }}>
                                    ({activeStrapOption.addonPrice}$)
                                </div>
                            )}
                            {activeStrapOption.addonSubtitle && (
                                <div style={{ fontSize: '0.8rem', color: '#1A110B', marginTop: '4px', marginBottom: '16px' }}>
                                    {activeStrapOption.addonSubtitle}
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <div
                                    style={{ width: '44px', height: '24px', borderRadius: '24px', backgroundColor: isAddonActive ? '#1A110B' : '#E2E8F0', padding: '2px', cursor: isOrderFinished ? 'default' : 'pointer', transition: 'background-color 0.2s', display: 'flex', alignItems: 'center' }}
                                    onClick={() => { if (isOrderFinished) return; updateValue(textVal, !isAddonActive); }}
                                >
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', transform: isAddonActive ? 'translateX(20px)' : 'translateX(0)', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    } else {
        const rawDropVal = (typeof tempStrapValue === 'string' && tempStrapValue.includes('||'))
            ? tempStrapValue.slice(0, tempStrapValue.indexOf('||'))
            : (tempStrapValue || '');
        const customDropText = (typeof tempStrapValue === 'string' && tempStrapValue.includes('||'))
            ? tempStrapValue.slice(tempStrapValue.indexOf('||') + 2)
            : '';
        const selectedDropChoice = (activeStrapOption.choices || []).find(c => (c.value || c._id) === rawDropVal);
        const needsCustomInput = selectedDropChoice?.hasCustomInput;

        body = (
            <div style={{ padding: '0 20px 20px 20px', marginTop: '20px' }}>
                <div style={{ position: 'relative' }}>
                    <select
                        className="form-select shadow-sm"
                        value={rawDropVal}
                        onChange={(e) => { if (isOrderFinished) return; setTempStrapValue(e.target.value); }}
                        disabled={isOrderFinished}
                        style={{ padding: '14px 16px', borderRadius: '12px', border: '1.5px solid #D6C5B3', fontSize: '0.95rem', color: '#2E2016', backgroundColor: '#fff', boxShadow: 'none', cursor: 'pointer', fontWeight: '500', appearance: 'none', width: '100%' }}
                    >
                        <option value="" disabled>-- Select {activeStrapOption.label} --</option>
                        {(activeStrapOption.choices || []).map((choice, idx) => {
                            const choiceKey = choice.value || choice._id || String(idx);
                            const priceText = choice.priceAddon > 0 ? ` (+ $${choice.priceAddon})` : '';
                            return (
                                <option key={choiceKey} value={choiceKey}>
                                    {choice.label}{priceText}
                                </option>
                            );
                        })}
                    </select>
                    <ChevronDown size={14} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#8B7D6B', pointerEvents: 'none' }} />
                </div>
                {needsCustomInput && (
                    <div style={{ marginTop: '12px' }}>
                        <input
                            type="text"
                            value={customDropText}
                            onChange={(e) => { if (isOrderFinished) return; setTempStrapValue(`${rawDropVal}||${e.target.value}`); }}
                            disabled={isOrderFinished}
                            placeholder={selectedDropChoice?.customInputPlaceholder || 'Enter your value...'}
                            autoFocus
                            style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: `1.5px solid ${customDropText.trim() ? '#C5A075' : '#D6C5B3'}`, fontSize: '0.95rem', color: '#2E2016', backgroundColor: '#fff', outline: 'none', transition: 'border-color 0.2s', boxShadow: customDropText.trim() ? '0 0 0 3px rgba(197,160,117,0.15)' : 'none' }}
                        />
                        {!customDropText.trim() && (
                            <div style={{ fontSize: '0.75rem', color: '#C5A075', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <AlertCircle size={13} />
                                Please enter a value to continue
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    const isDesktop = window.innerWidth > 768;
    const useVerticalTabs = isDesktop
        && activeStrapOption.type === 'color-picker'
        && !!(activeStrapOption.useTabs && activeStrapOption.tabs?.length > 0);

    const rawForCheck = (typeof tempStrapValue === 'string' && tempStrapValue.includes('||'))
        ? tempStrapValue.slice(0, tempStrapValue.indexOf('||'))
        : (tempStrapValue || '');
    const customForCheck = (typeof tempStrapValue === 'string' && tempStrapValue.includes('||'))
        ? tempStrapValue.slice(tempStrapValue.indexOf('||') + 2)
        : '';
    const choiceForCheck = (activeStrapOption?.choices || []).find(c => (c.value || c._id) === rawForCheck);
    const isCustomInputBlocked = !!(choiceForCheck?.hasCustomInput && !customForCheck.trim());
    const isBlocked = tempStrapValue === null || isCustomInputBlocked;

    return (
        <>
            <div className="cfg-sec-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="cfg-sec-title" style={{ display: 'flex', alignItems: 'center' }}>
                    {activeStrapOption.label}
                    {activeStrapOption.optionTooltip && (
                        <Tooltip text={activeStrapOption.optionTooltip}>
                            <span style={{ border: '1px solid #C5A075', borderRadius: '50%', width: '15px', height: '15px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#C5A075', fontSize: '9px', fontWeight: '700', cursor: 'help', flexShrink: 0, lineHeight: 1, marginLeft: '4px', letterSpacing: 'normal', textTransform: 'none', transform: 'translateY(-1px)' }}>?</span>
                        </Tooltip>
                    )}
                </div>
                {activeStrapOption.description && activeStrapOption.descriptionTitle && (
                    <button
                        type="button"
                        onClick={() => setShowDescModal(true)}
                        style={{ background: 'none', border: 'none', textDecoration: 'underline', color: '#1E2335', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', padding: '0' }}
                    >
                        {activeStrapOption.descriptionTitle}
                    </button>
                )}
            </div>

            {useVerticalTabs ? (
                <div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
                    <div style={{ width: '160px', flexShrink: 0, background: '#F8F6F3', borderRight: '1px solid #EAE4DA', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                        {activeStrapOption.tabs.map(tab => (
                            <button
                                key={tab}
                                type="button"
                                onClick={() => setActiveSubTab(tab)}
                                style={{ width: '100%', padding: '18px 14px', textAlign: 'center', fontSize: '0.82rem', lineHeight: 1.4, fontWeight: activeSubTab === tab ? '700' : '500', color: activeSubTab === tab ? '#1A110B' : '#8B7D6B', background: activeSubTab === tab ? '#FFFFFF' : 'transparent', border: 'none', borderBottom: '1px solid #EEE8DE', boxShadow: activeSubTab === tab ? '2px 0 10px rgba(0,0,0,0.03)' : 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', background: '#FFFFFF' }}>
                        {body}
                        {activeStrapOption.contentGuide?.trim() && (
                            <div className="cfg-guide-content" dangerouslySetInnerHTML={{ __html: activeStrapOption.contentGuide }} />
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {activeStrapOption.useTabs && activeStrapOption.tabs && activeStrapOption.tabs.length > 0 && (
                        <div className="cfg-sub-tabs">
                            {activeStrapOption.tabs.map(tab => (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`cfg-sub-tab-btn${activeSubTab === tab ? ' active' : ''}`}
                                    onClick={() => setActiveSubTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                        {body}
                        {activeStrapOption.contentGuide && (
                            <div className="cfg-guide-content" dangerouslySetInnerHTML={{ __html: activeStrapOption.contentGuide }} />
                        )}
                    </div>
                </>
            )}

            <div className="cfg-confirm-footer">
                <button
                    className="cfg-confirm-btn"
                    onClick={onBack}
                    style={{ background: '#F5F0EA', color: '#6B5A4A', borderRight: '1px solid #EAE4DA' }}
                >
                    Back
                </button>
                <button
                    className="cfg-confirm-btn"
                    onClick={onConfirm}
                    disabled={isBlocked}
                    style={{ background: !isBlocked ? '#1A110B' : '#D0C8BF', color: '#FFFFFF' }}
                >
                    Confirm
                </button>
            </div>

            {zoomImgSrc && (
                <div
                    onClick={() => setZoomImgSrc(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
                >
                    <LazyImage src={zoomImgSrc} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} />
                    <button
                        onClick={() => setZoomImgSrc(null)}
                        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 40, height: 40, fontSize: '1.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <X size={20} />
                    </button>
                </div>
            )}

            <InfoModal
                isOpen={showDescModal}
                onClose={() => setShowDescModal(false)}
                category={activeStrapOption?.label}
                title={activeStrapOption?.descriptionTitle || activeStrapOption?.label}
                maxWidth="500px"
            >
                {activeStrapOption && activeStrapOption.description && (
                    <div
                        style={{ color: '#4A3728', fontSize: '0.95rem', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: activeStrapOption.description }}
                    />
                )}
            </InfoModal>
        </>
    );
};

export default StrapOptionPanel;
